import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ShinyButton } from "../../../../global-account/react/index.js";
import { cn } from "../../../../shared/utils/cn.js";
import { ChevronDown } from "lucide-react";
import { useState } from "react";
// Fee tier definitions
const CRYPTO_FEE_TIERS = [{
  min: 0,
  max: 10,
  bps: 120,
  label: "$0 – $10"
}, {
  min: 10,
  max: 500,
  bps: 80,
  label: "$10 – $500"
}, {
  min: 500,
  max: 5000,
  bps: 60,
  label: "$500 – $5,000"
}, {
  min: 5000,
  max: 50000,
  bps: 40,
  label: "$5,000 – $50,000"
}, {
  min: 50000,
  max: Infinity,
  bps: 28,
  label: "$50,000+"
}];
const FIAT_FEE_TIERS = [{
  min: 0.01,
  max: 25,
  fee: "$1",
  label: "$0.01 – $25"
}, {
  min: 25.01,
  max: 250,
  fee: "2%",
  label: "$25.01 – $250"
}, {
  min: 251,
  max: Infinity,
  fee: "3%",
  label: "$251+"
}];
// Whale discount tiers based on $ANY holdings
const WHALE_DISCOUNT_TIERS = [{
  minAny: 100000,
  discountPercent: 50,
  label: "Tier 1: 100k $ANY"
}, {
  minAny: 500000,
  discountPercent: 75,
  label: "Tier 2: 500k $ANY"
}, {
  minAny: 1000000,
  discountPercent: 100,
  label: "Tier 3: 1M+ $ANY"
}];
export function FeeDetailPanel({
  fee,
  transactionAmountUsd,
  onBack,
  classes
}) {
  // Detect if this is a fiat onramp order (Stripe) vs regular crypto swap
  // stripeweb2_fee = Stripe/fiat onramp (uses FIAT_FEE_TIERS)
  // standard_fee = Regular crypto swap (uses CRYPTO_FEE_TIERS)
  const isStripeFee = fee.type === "stripeweb2_fee";
  // Convert basis points to percentage
  const bpsToPercent = bps => (bps / 100).toFixed(2);
  // Check if discount is active
  const hasWhaleDiscount = fee.anyspendWhaleDiscountBps > 0;
  const hasPartnerDiscount = fee.anyspendPartnerDiscountBps > 0;
  // Find current tier based on transaction amount
  const getCurrentCryptoTier = amount => {
    if (!amount) return null;
    return CRYPTO_FEE_TIERS.find(tier => amount >= tier.min && amount < tier.max);
  };
  const getCurrentFiatTier = amount_0 => {
    if (!amount_0) return null;
    return FIAT_FEE_TIERS.find(tier_0 => amount_0 >= tier_0.min && amount_0 < tier_0.max);
  };
  const currentCryptoTier = getCurrentCryptoTier(transactionAmountUsd);
  const currentFiatTier = getCurrentFiatTier(transactionAmountUsd);
  // The whale discount is a percentage discount on the base fee itself
  // Example: 50% discount on 80 bps fee = 40 bps discount, final fee = 40 bps
  // So: finalFee = baseFee - (baseFee * discountPercent / 100)
  // Which means: discountPercent = ((baseFee - finalFee) / baseFee) * 100
  const baseFee = fee.type === "standard_fee" ? fee.anyspendFeeBps : 0;
  // The whale discount percentage (50%, 75%, or 100%)
  const whaleDiscountPercent = baseFee > 0 && hasWhaleDiscount ? Math.round((baseFee - fee.finalFeeBps) / baseFee * 100) : 0;
  // Determine which whale tier based on the discount percentage
  const currentWhaleTier = WHALE_DISCOUNT_TIERS.find(tier_1 => Math.abs(whaleDiscountPercent - tier_1.discountPercent) <= 5);
  // Calculate partner discount percentage
  const partnerDiscountPercent = baseFee > 0 && hasPartnerDiscount ? Math.round(fee.anyspendPartnerDiscountBps / baseFee * 100) : 0;
  // State for expanding tier lists
  const [showAllFeeTiers, setShowAllFeeTiers] = useState(false);
  const [showAllDiscountTiers, setShowAllDiscountTiers] = useState(false);
  return _jsx("div", {
    className: classes?.container || "mx-auto flex w-full max-w-[460px] flex-col items-center gap-3 px-5",
    children: _jsxs("div", {
      className: "flex w-full flex-col gap-3",
      children: [_jsx("div", {
        className: "text-center",
        children: _jsx("h3", {
          className: classes?.title || "text-as-primary text-lg font-bold",
          children: "Fee Breakdown"
        })
      }), _jsxs("div", {
        className: classes?.tierCard || "bg-as-surface-secondary border-as-border-secondary rounded-2xl border p-4",
        children: [_jsx("h4", {
          className: classes?.tierCardTitle || "text-as-primary mb-3 text-sm font-semibold",
          children: isStripeFee ? "Fiat Fee Schedule" : "Base Fee Schedule"
        }), _jsx("div", {
          className: "space-y-1.5",
          children: isStripeFee ? FIAT_FEE_TIERS.map((tier_2, idx) => {
            const isCurrentTier = currentFiatTier?.label === tier_2.label;
            const currentTierIndex = FIAT_FEE_TIERS.findIndex(t => t.label === currentFiatTier?.label);
            // Show all tiers if expanded, otherwise show up to current tier
            if (!showAllFeeTiers && currentTierIndex !== -1 && idx > currentTierIndex) {
              return null;
            }
            return _jsxs("div", {
              className: cn(isCurrentTier ? classes?.tierRowActive || "bg-as-brand/10 text-as-brand flex items-center justify-between rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors" : classes?.tierRow || "text-as-primary/60 flex items-center justify-between rounded-lg px-3 py-2.5 text-sm transition-colors"),
              children: [_jsx("span", {
                className: classes?.tierLabel,
                children: tier_2.label
              }), _jsxs("span", {
                className: classes?.tierValue,
                children: ["Credit Card Fee + ", tier_2.fee]
              })]
            }, idx);
          }) : CRYPTO_FEE_TIERS.map((tier_3, idx_0) => {
            const isCurrentTier_0 = currentCryptoTier?.label === tier_3.label;
            const currentTierIndex_0 = CRYPTO_FEE_TIERS.findIndex(t_0 => t_0.label === currentCryptoTier?.label);
            // Show all tiers if expanded, otherwise show up to current tier
            if (!showAllFeeTiers && currentTierIndex_0 !== -1 && idx_0 > currentTierIndex_0) {
              return null;
            }
            return _jsxs("div", {
              className: cn(isCurrentTier_0 ? classes?.tierRowActive || "bg-as-brand/10 text-as-brand flex items-center justify-between rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors" : classes?.tierRow || "text-as-primary/60 flex items-center justify-between rounded-lg px-3 py-2.5 text-sm transition-colors"),
              children: [_jsx("span", {
                className: classes?.tierLabel,
                children: tier_3.label
              }), _jsxs("span", {
                className: classes?.tierValue,
                children: [bpsToPercent(tier_3.bps), "%"]
              })]
            }, idx_0);
          })
        }), (() => {
          const currentTierIndex_1 = isStripeFee ? FIAT_FEE_TIERS.findIndex(t_1 => t_1.label === currentFiatTier?.label) : CRYPTO_FEE_TIERS.findIndex(t_2 => t_2.label === currentCryptoTier?.label);
          const totalTiers = isStripeFee ? FIAT_FEE_TIERS.length : CRYPTO_FEE_TIERS.length;
          const hasMoreTiers = currentTierIndex_1 !== -1 && currentTierIndex_1 < totalTiers - 1;
          if (hasMoreTiers) {
            return _jsxs("button", {
              onClick: () => setShowAllFeeTiers(!showAllFeeTiers),
              className: classes?.expandButton || "text-as-primary/60 hover:text-as-primary mt-2 flex w-full items-center justify-center gap-1 text-xs transition-colors",
              children: [_jsx("span", {
                children: showAllFeeTiers ? "Show less" : "Show higher tiers"
              }), _jsx(ChevronDown, {
                className: cn("h-3.5 w-3.5 transition-transform", showAllFeeTiers && "rotate-180")
              })]
            });
          }
          return null;
        })()]
      }), !isStripeFee && _jsxs("div", {
        className: "bg-as-surface-secondary border-as-border-secondary rounded-2xl border p-4",
        children: [_jsx("h4", {
          className: "text-as-primary mb-3 text-sm font-semibold",
          children: hasWhaleDiscount ? "Whale Discount Tiers" : hasPartnerDiscount ? "Partner Discount" : "Discount Tiers"
        }), _jsx("div", {
          className: "space-y-1.5",
          children: hasPartnerDiscount && !hasWhaleDiscount ? _jsxs("div", {
            className: "flex items-center justify-between rounded-lg bg-green-500/10 px-3 py-2.5 text-sm font-semibold text-green-600",
            children: [_jsx("span", {
              children: "Partner Discount"
            }), _jsxs("span", {
              children: [partnerDiscountPercent, "% discount"]
            })]
          }) : _jsxs(_Fragment, {
            children: [WHALE_DISCOUNT_TIERS.map((tier_4, idx_1) => {
              const isCurrentTier_1 = currentWhaleTier?.label === tier_4.label;
              const currentTierIndex_2 = WHALE_DISCOUNT_TIERS.findIndex(t_3 => t_3.label === currentWhaleTier?.label);
              // If no whale discount, show only first tier; otherwise show up to current tier
              if (!showAllDiscountTiers) {
                if (!hasWhaleDiscount && idx_1 > 0) {
                  return null;
                }
                if (hasWhaleDiscount && currentTierIndex_2 !== -1 && idx_1 > currentTierIndex_2) {
                  return null;
                }
              }
              return _jsxs("div", {
                className: cn("flex items-center justify-between rounded-lg px-3 py-2.5 text-sm transition-colors", isCurrentTier_1 ? "bg-green-500/10 font-semibold text-green-600" : "text-as-primary/60"),
                children: [_jsx("span", {
                  children: tier_4.label
                }), _jsxs("span", {
                  children: [tier_4.discountPercent, "% discount"]
                })]
              }, idx_1);
            }), (() => {
              const currentTierIndex_3 = WHALE_DISCOUNT_TIERS.findIndex(t_4 => t_4.label === currentWhaleTier?.label);
              const hasMoreTiers_0 = hasWhaleDiscount ? currentTierIndex_3 !== -1 && currentTierIndex_3 < WHALE_DISCOUNT_TIERS.length - 1 : WHALE_DISCOUNT_TIERS.length > 1;
              if (hasMoreTiers_0) {
                return _jsxs("button", {
                  onClick: () => setShowAllDiscountTiers(!showAllDiscountTiers),
                  className: "text-as-primary/60 hover:text-as-primary mt-2 flex w-full items-center justify-center gap-1 text-xs transition-colors",
                  children: [_jsx("span", {
                    children: showAllDiscountTiers ? "Show less" : "Show all tiers"
                  }), _jsx(ChevronDown, {
                    className: cn("h-3.5 w-3.5 transition-transform", showAllDiscountTiers && "rotate-180")
                  })]
                });
              }
              return null;
            })()]
          })
        })]
      }), transactionAmountUsd && _jsx("div", {
        className: classes?.summaryCard || "bg-as-surface-secondary border-as-border-secondary rounded-2xl border p-4",
        children: _jsxs("div", {
          className: "space-y-2 text-sm",
          children: [_jsxs("div", {
            className: classes?.summaryRow || "flex items-center justify-between",
            children: [_jsx("span", {
              className: classes?.summaryLabel || "text-as-secondary",
              children: "Transaction"
            }), _jsxs("span", {
              className: classes?.summaryValue || "text-as-primary font-semibold",
              children: ["$", transactionAmountUsd.toFixed(2)]
            })]
          }), isStripeFee && _jsxs(_Fragment, {
            children: [_jsxs("div", {
              className: "flex items-center justify-between",
              children: [_jsxs("span", {
                className: "text-as-secondary",
                children: ["Credit Card Fee (", fee.stripeFeeBps ? `${bpsToPercent(fee.stripeFeeBps)}%` : "0%", " + $", fee.stripeFeeUsd?.toFixed(2) || "0.00", ")"]
              }), _jsxs("span", {
                className: "text-as-primary font-medium",
                children: ["$", (transactionAmountUsd * (fee.stripeFeeBps || 0) / 10000 + (fee.stripeFeeUsd || 0)).toFixed(2)]
              })]
            }), _jsxs("div", {
              className: "flex items-center justify-between",
              children: [_jsxs("span", {
                className: "text-as-secondary",
                children: ["AnySpend Fee (", fee.anyspendFeeBps ? `${bpsToPercent(fee.anyspendFeeBps)}%` : "0%", fee.anyspendFeeUsd && fee.anyspendFeeUsd > 0 ? ` + $${fee.anyspendFeeUsd.toFixed(2)}` : "", ")"]
              }), _jsxs("span", {
                className: "text-as-primary font-medium",
                children: ["$", (transactionAmountUsd * (fee.anyspendFeeBps || 0) / 10000 + (fee.anyspendFeeUsd || 0)).toFixed(2)]
              })]
            }), _jsx("div", {
              className: classes?.summaryDivider || "border-as-border-secondary border-t pt-2",
              children: _jsxs("div", {
                className: classes?.totalRow || "flex items-center justify-between",
                children: [_jsx("span", {
                  className: classes?.totalLabel || "text-as-primary font-semibold",
                  children: "Total Fee"
                }), _jsxs("span", {
                  className: classes?.totalValue || "text-as-brand font-semibold",
                  children: ["$", (transactionAmountUsd * (fee.finalFeeBps || 0) / 10000 + (fee.finalFeeUsd || 0)).toFixed(2)]
                })]
              })
            })]
          }), !isStripeFee && currentCryptoTier && _jsxs(_Fragment, {
            children: [_jsxs("div", {
              className: classes?.summaryRow || "flex items-center justify-between",
              children: [_jsxs("span", {
                className: classes?.summaryLabel || "text-as-secondary",
                children: ["Base Fee (", bpsToPercent(currentCryptoTier.bps), "%)"]
              }), _jsxs("span", {
                className: classes?.summaryValue || "text-as-primary font-medium",
                children: ["$", (transactionAmountUsd * currentCryptoTier.bps / 10000).toFixed(2)]
              })]
            }), hasWhaleDiscount && currentWhaleTier && _jsxs("div", {
              className: classes?.summaryRow || "flex items-center justify-between",
              children: [_jsxs("span", {
                className: "text-green-600",
                children: ["Discount (", currentWhaleTier.discountPercent, "% off)"]
              }), _jsxs("span", {
                className: "font-medium text-green-600",
                children: ["-$", (transactionAmountUsd * baseFee * currentWhaleTier.discountPercent / 100 / 10000).toFixed(2)]
              })]
            }), hasPartnerDiscount && _jsxs("div", {
              className: classes?.summaryRow || "flex items-center justify-between",
              children: [_jsxs("span", {
                className: "text-green-600",
                children: ["Partner Discount (", partnerDiscountPercent, "% off)"]
              }), _jsxs("span", {
                className: "font-medium text-green-600",
                children: ["-$", (transactionAmountUsd * baseFee * partnerDiscountPercent / 100 / 10000).toFixed(2)]
              })]
            }), _jsx("div", {
              className: classes?.summaryDivider || "border-as-border-secondary border-t pt-2",
              children: _jsxs("div", {
                className: classes?.totalRow || "flex items-center justify-between",
                children: [_jsx("span", {
                  className: classes?.totalLabel || "text-as-primary font-semibold",
                  children: "Total Fee"
                }), _jsxs("span", {
                  className: classes?.totalValue || "text-as-brand font-semibold",
                  children: ["$", (transactionAmountUsd * fee.finalFeeBps / 10000).toFixed(2)]
                })]
              })
            })]
          })]
        })
      }), _jsxs(ShinyButton, {
        accentColor: "hsl(var(--as-brand))",
        onClick: onBack,
        className: classes?.backButton || cn("as-main-button !bg-as-brand relative w-full"),
        textClassName: cn("text-white"),
        children: ["Back to ", isStripeFee ? "Payment" : "Swap"]
      })]
    })
  });
}